import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import { createApiClient } from '@/lib/api/client';

import {
    passkeyAssociationResponseSchema,
    passkeyCredentialSchema,
    passkeyListResponseSchema,
    passkeyRegistrationOptionsResponseSchema,
} from './passkeySchemas';
import type {
    PasskeyAssociationResponse,
    PasskeyCredential,
    PasskeyListResponse,
    PasskeyRegistrationOptionsResponse,
    RequestPasskeyAssociation,
    RequestPasskeyRegistrationOptions,
    VerifyPasskeyRegistrationRequest,
} from './passkeyTypes';
import {
    accessTokenResponseSchema,
    logoutResponseSchema,
    passkeyDiscoveryOptionsResponseSchema,
    passkeyOwnerDiscoveryResponseSchema,
    passkeyTradingSessionChallengeResponseSchema,
    tradingSessionChallengeResponseSchema,
    tradingSessionCookieProbeResponseSchema,
    tradingSessionFinishResponseSchema,
    tradingSessionStatusSchema,
} from './schemas';
import type {
    AccessTokenResponse,
    FinishTradingSessionRequest,
    PasskeyDiscoveryOptionsResponse,
    PasskeyOwnerDiscoveryResponse,
    SerializedPasskeyAssertionProof,
    StartTradingSessionRequest,
    TradingSessionCookieProbeResponse,
    TradingSessionFinishResponse,
    TradingSessionStartResponse,
    TradingSessionStatus,
} from './types';

const WOLFSWAP_BASE_URL =
    import.meta.env.VITE_WOLFSWAP_API_URL ?? 'https://api.wolfswap.app';

const tradingSessionApi = createApiClient(WOLFSWAP_BASE_URL, {
    credentials: 'include',
    headers: { 'X-Snappy-Client': 'web-v1' },
});

export async function startTradingSession(
    request: StartTradingSessionRequest,
): Promise<TradingSessionStartResponse> {
    const { method, ...rest } = request;
    const anchorChainId = rest.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const challenge = { ...rest, anchorChainId };
    const path =
        method === 'eip712'
            ? 'v2/trading-session/challenge'
            : 'v2/trading-session/passkey/options';
    const data = await tradingSessionApi.post(path, { json: challenge }).json();
    if (method === 'eip712') {
        return {
            method,
            ...assertAnchorChainId(
                tradingSessionChallengeResponseSchema.parse(data),
                anchorChainId,
            ),
        };
    }
    return {
        method,
        ...assertAnchorChainId(
            passkeyTradingSessionChallengeResponseSchema.parse(data),
            anchorChainId,
        ),
    };
}

/**
 * The signed `domain.chainId` is what really binds the anchor, and the ceremony
 * re-derives the whole typed data before signing. This is the cheap early stop:
 * a response that answered for a different anchor than the one asked for is
 * refused before the wallet ever opens.
 */
function assertAnchorChainId<T extends { anchorChainId?: AnchorChainId }>(
    response: T,
    expected: AnchorChainId,
): T {
    if (
        response.anchorChainId !== undefined &&
        response.anchorChainId !== expected
    ) {
        throw new Error(
            'The server answered for a different network than the one ' +
                'requested; refusing to sign.',
        );
    }
    return response;
}

export async function startPasskeyDiscovery(): Promise<PasskeyDiscoveryOptionsResponse> {
    const data = await tradingSessionApi
        .post('v2/trading-session/passkey/discovery/options', { json: {} })
        .json();
    return passkeyDiscoveryOptionsResponseSchema.parse(data);
}

export async function discoverPasskeyOwner(
    assertion: SerializedPasskeyAssertionProof,
): Promise<PasskeyOwnerDiscoveryResponse> {
    const data = await tradingSessionApi
        .post('v2/trading-session/passkey/discovery', { json: assertion })
        .json();
    return passkeyOwnerDiscoveryResponseSchema.parse(data);
}

export async function finishTradingSession(
    request: FinishTradingSessionRequest,
): Promise<TradingSessionFinishResponse> {
    const { method, ...proof } = request;
    const path =
        method === 'eip712'
            ? 'v2/trading-session/eip712'
            : 'v2/trading-session/passkey';
    const result = tradingSessionFinishResponseSchema.parse(
        await tradingSessionApi.post(path, { json: proof }).json(),
    );
    if (result.method !== method) {
        throw new Error('The server returned the wrong trading-session method.');
    }
    return result;
}

export async function fetchTradingSession(): Promise<TradingSessionStatus> {
    const data = await tradingSessionApi.get('v2/trading-session').json();
    return tradingSessionStatusSchema.parse(data);
}

/**
 * Cross-site cookie liveness check. Same origin rules as
 * {@link fetchTradingSession}: the answer is only meaningful because the call
 * carries (and the response re-sets) the short-lived probe cookie.
 */
export async function fetchTradingSessionCookieProbe(): Promise<TradingSessionCookieProbeResponse> {
    const data = await tradingSessionApi.get('v2/trading-session/probe').json();
    return tradingSessionCookieProbeResponseSchema.parse(data);
}

export async function logoutTradingSession(): Promise<void> {
    const data = await tradingSessionApi
        .post('v2/trading-session/logout')
        .json();
    logoutResponseSchema.parse(data);
}

export async function exchangeAccessToken(): Promise<AccessTokenResponse> {
    const data = await tradingSessionApi.post('v2/auth/access-token').json();
    return accessTokenResponseSchema.parse(data);
}

export async function requestPasskeyRegistrationOptions(
    request: RequestPasskeyRegistrationOptions,
): Promise<PasskeyRegistrationOptionsResponse> {
    const data = await tradingSessionApi
        .post('v2/passkeys/registration/options', { json: request })
        .json();
    return passkeyRegistrationOptionsResponseSchema.parse(data);
}

export async function verifyPasskeyRegistration(
    request: VerifyPasskeyRegistrationRequest,
): Promise<PasskeyCredential> {
    const data = await tradingSessionApi
        .post('v2/passkeys/registration/verify', { json: request })
        .json();
    return passkeyCredentialSchema.parse(data);
}

export async function listPasskeys(): Promise<PasskeyListResponse> {
    const data = await tradingSessionApi.get('v2/passkeys').json();
    return passkeyListResponseSchema.parse(data);
}

export async function requestPasskeyAssociation(
    id: string,
    request: RequestPasskeyAssociation,
): Promise<PasskeyAssociationResponse> {
    const data = await tradingSessionApi
        .post(`v2/passkeys/${encodeURIComponent(id)}/association`, {
            json: request,
        })
        .json();
    return passkeyAssociationResponseSchema.parse(data);
}

export async function confirmPasskey(id: string): Promise<PasskeyCredential> {
    const data = await tradingSessionApi
        .post(`v2/passkeys/${encodeURIComponent(id)}/confirm`)
        .json();
    return passkeyCredentialSchema.parse(data);
}

export async function confirmPasskeyRemoval(
    id: string,
): Promise<PasskeyCredential> {
    const data = await tradingSessionApi
        .post(`v2/passkeys/${encodeURIComponent(id)}/removal/confirm`)
        .json();
    return passkeyCredentialSchema.parse(data);
}
